// import { switchAnatomy } from "@chakra-ui/anatomy";
import { extendTheme } from "@chakra-ui/react";

import { CardStyle } from "./components/cardStyle";
import { config } from "./config";

type ColorMode = "light" | "dark";

// Graycolor = "#E2DADB" Original default color
export const theme = extendTheme({
  colors: {
    brand: {
      primary: "#41bda3",
      secondary: "#79c144",
      cta: "#54b848",
      background_light: "#e3f1e1",
      sage_green: "#98d1a8",
      dark_green: "#54b848",
      //Optional suggestions
      forest_green: "#2c7a44",
      mint_green: "#c2e6cc",
      olive_green: "#8fa977",
      //Off green suggestions
      coral_accent: "#ff7f6b",
      ocean_blue: "#4190b7",
      sand_beige: "#f5e6d3",
      //UI support
      success: "#2d9d78",
      warning: "#f0b429",
      error: "#dc3545",
      info: "#17a2b8"
    }
  },
  textStyles: {
    body: {
      fontFamily: "Inter Variable",
      fontWeight: "400",
      color: "gray.800",
      fontSize: ".90rem",
      lineHeight: "tall"
    },
    sectionTitle: {
      fontSize: "lg",
      fontWeight: "medium",
      marginBottom: "2",
      color: "brand.dark_green"
    }
  },
  styles: {
    global: ({ colorMode }: { colorMode: ColorMode }) => ({
      body: {
        color: colorMode === "dark" ? "gray.100" : "gray.800",
        bg: colorMode === "dark" ? "gray.900" : "white"
      }
    })
  },
  fonts: {
    heading: "Roboto Flex Variable, sans-serif",
    body: "Inter Variable, sans-serif"
  },
  components: {
    PhChart: CardStyle,
    PhTestControls: {
      baseStyle: {
        border: "1px solid",
        borderColor: "brand.sage_green",
        borderRadius: "md",
        p: 3,
        bg: "white",
        color: "gray.800",
        _dark: {
          bg: "gray.800",
          color: "gray.100"
        },
        ".trend-control-box": {
          border: "1px solid",
          borderColor: "brand.sage_green",
          borderRadius: "sm",
          p: 2,
          bg: "gray.50",
          _dark: {
            bg: "gray.700"
          }
        }
      }
    },
    Link: {
      baseStyle: ({ colorMode }: { colorMode: ColorMode }) => ({
        color: colorMode === "dark" ? "brand.sage_green" : "brand.primary",
        _hover: {
          color: colorMode === "dark" ? "brand.dark_green" : "brand.olive_green",
          textDecoration: "none"
        }
      })
    },
    Button: {
      variants: {
        solid: ({ colorMode }: { colorMode: ColorMode }) => ({
          bg: colorMode === "dark" ? "brand.dark_green" : "brand.primary",
          color: colorMode === "dark" ? "white" : "black",
          _hover: {
            bg: colorMode === "dark" ? "brand.forest_green" : "brand.olive_green"
          }
        })
      }
    },
    ResultsGrid: {
      baseStyle: ({ colorMode }: { colorMode: ColorMode }) => ({
        bg: colorMode === "dark" ? "gray.800" : "white",
        color: colorMode === "dark" ? "gray.100" : "gray.800"
      })
    },
    Container: {
      baseStyle: ({ colorMode }: { colorMode: ColorMode }) => ({
        bg: colorMode === "dark" ? "gray.800" : "white",
        color: colorMode === "dark" ? "gray.100" : "gray.800"
      }),
      sizes: {
        sm: {
          maxWidth: "640px"
        },
        md: {
          maxWidth: "960px"
        },
        lg: {
          maxWidth: "1280px"
        },
        xl: {
          maxWidth: "1600px"
        }
      }
    },
    Select: {
      variants: {
        outline: {
          field: {
            borderColor: "gray.300",
            borderWidth: "1px",
            _focus: {
              borderColor: "blue.500",
              boxShadow: "0 0 0 1px #3182ce"
            }
          }
        }
      }
    },
    Sidebar: {
      baseStyle: ({ colorMode }: { colorMode: ColorMode }) => ({
        bg: colorMode === "dark" ? "gray.800" : "gray.50",
        color: colorMode === "dark" ? "gray.100" : "gray.800",
        _hover: {
          bg: colorMode === "dark" ? "gray.700" : "gray.100",
          color: colorMode === "dark" ? "gray.100" : "gray.800"
        }
      })
    },
    Input: {
      baseStyle: {
        field: {
          _focus: {
            borderColor: "brand.primary",
            boxShadow: "0 0 0 1px brand.primary"
          }
        }
      },
      variants: {
        outline: ({ colorMode }: { colorMode: ColorMode }) => ({
          field: {
            bg: colorMode === "dark" ? "gray.800" : "white",
            borderColor: colorMode === "dark" ? "gray.600" : "gray.300",
            _hover: {
              borderColor: colorMode === "dark" ? "gray.500" : "gray.400"
            },
            _focus: {
              borderColor: "brand.primary",
              boxShadow: "0 0 0 1px var(--chakra-colors-brand-primary)"
            }
          }
        })
      },
      defaultProps: {
        variant: "outline"
      }
    },
    Navbar: {
      baseStyle: {
        bg: "brand.background_light",
        color: "white",
        borderBottomColor: "gray.200",
        borderBottomWidth: "1px",
        px: 4,
        w: "100%"
      },
      variants: {
        solid: {
          bg: "brand.primary",
          color: "white"
        },
        transparent: {
          bg: "transparent",
          color: "gray.800"
        }
      },
      defaultProps: {
        variant: "solid"
      }
    },
    Menu: {
      baseStyle: ({ colorMode }: { colorMode: ColorMode }) => ({
        list: {
          bg: colorMode === "dark" ? "gray.800" : "white",
          borderColor: colorMode === "dark" ? "gray.700" : "gray.200"
        },
        item: {
          bg: colorMode === "dark" ? "gray.800" : "white",
          color: colorMode === "dark" ? "gray.100" : "gray.800",
          _hover: {
            bg: colorMode === "dark" ? "gray.700" : "gray.100"
          }
        }
      })
    }
  },
  config
});
